import { DebugWidget, createWidgetContainer } from './DebugOverlay';
import { DEFAULT_DENSITY_ALPHA } from '../materials/PointCloudMaterial';
import type { Viewer } from '../viewer/viewer';

/** Slider bounds. Alpha is tiny by nature (points stack additively), so the slider
 * is logarithmic: equal slider travel = equal multiplicative change in alpha.
 * The low end only works on the float accumulation path (see
 * `viewer.xrayDensityHighPrecision`); the 8-bit fallback floors at ~0.002. */
const MIN_ALPHA = 0.0001;
const MAX_ALPHA = 0.2;
const SLIDER_STEPS = 300;

const alphaToSlider = (alpha: number): number => {
  const t = Math.log(alpha / MIN_ALPHA) / Math.log(MAX_ALPHA / MIN_ALPHA);
  return Math.round(Math.max(0, Math.min(1, t)) * SLIDER_STEPS);
};

const sliderToAlpha = (position: number): number => {
  const t = position / SLIDER_STEPS;
  return MIN_ALPHA * Math.pow(MAX_ALPHA / MIN_ALPHA, t);
};

/**
 * Debug widget tuning the X-ray (density mode) per-point alpha. Density mode
 * renders every point as white with a small alpha and lets them stack, so this
 * value controls how many overlapping points saturate a pixel to white.
 * Writes through `viewer.xrayDensityAlpha`, which fans out to every point
 * cloud material and persists across reloads.
 */
export class XrayDensityDebugWidget implements DebugWidget {
  name = 'xray-density';
  container: HTMLElement;
  private readonly getViewer: () => Viewer | undefined;
  private readonly slider: HTMLInputElement;
  private readonly valueEl: HTMLElement;

  constructor(getViewer: () => Viewer | undefined) {
    this.getViewer = getViewer;
    this.container = createWidgetContainer('🩻 X-Ray Density');

    const description = document.createElement('div');
    description.style.cssText = 'margin-bottom: 8px; font-size: 12px; color: rgba(255, 255, 255, 0.7);';
    description.textContent = 'Per-point alpha in X-ray views. Lower = more points needed to saturate to white.';
    this.container.appendChild(description);

    if (getViewer()?.xrayDensityHighPrecision === false) {
      const warning = document.createElement('div');
      warning.style.cssText = 'margin-bottom: 8px; font-size: 12px; color: #ffb74d;';
      warning.textContent = 'GPU lacks float blending: 8-bit fallback, values below ~0.002 have no effect.';
      this.container.appendChild(warning);
    }

    const row = document.createElement('div');
    row.style.cssText = 'display: flex; align-items: center; gap: 8px;';

    this.slider = document.createElement('input');
    this.slider.type = 'range';
    this.slider.min = '0';
    this.slider.max = String(SLIDER_STEPS);
    this.slider.step = '1';
    this.slider.style.cssText = 'flex: 1; cursor: pointer;';
    this.slider.addEventListener('input', () => {
      this.applyAlpha(sliderToAlpha(Number(this.slider.value)));
    });

    this.valueEl = document.createElement('span');
    this.valueEl.style.cssText = 'min-width: 52px; text-align: right; font-variant-numeric: tabular-nums;';

    const resetButton = document.createElement('button');
    resetButton.textContent = 'Reset';
    resetButton.title = `Reset to default (${DEFAULT_DENSITY_ALPHA})`;
    resetButton.style.cssText = `
      padding: 2px 8px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 4px;
      color: #fff;
      cursor: pointer;
      font-family: inherit;
      font-size: inherit;
    `;
    resetButton.addEventListener('click', () => this.applyAlpha(DEFAULT_DENSITY_ALPHA));

    row.appendChild(this.slider);
    row.appendChild(this.valueEl);
    row.appendChild(resetButton);
    this.container.appendChild(row);

    this.syncFromViewer();
  }

  private applyAlpha(alpha: number): void {
    const viewer = this.getViewer();
    if (viewer) viewer.xrayDensityAlpha = alpha;
    this.syncFromViewer();
  }

  /** Reflect the viewer's current value (it may be changed elsewhere). */
  private syncFromViewer(): void {
    const alpha = this.getViewer()?.xrayDensityAlpha ?? DEFAULT_DENSITY_ALPHA;
    const sliderPosition = String(alphaToSlider(alpha));
    if (this.slider.value !== sliderPosition) this.slider.value = sliderPosition;
    this.valueEl.textContent = alpha.toFixed(4);
  }

  update(): void {
    this.syncFromViewer();
  }

  destroy(): void {
    this.container.remove();
  }
}
